import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { getSettings, getCompanies, createContact } from '../lib/api';
import { CardShell, LoadingScreen, PageHero, PhoneText, SectionTag } from '../components/common/BrandElements';

const fieldClass = 'mt-2 rounded-2xl border-raad-teal/15 bg-raad-porcelain text-raad-charcoal placeholder:text-raad-graphite/35 focus-visible:ring-raad-teal';

const ContactPage = () => {
  const { t, isRTL } = useLanguage();
  const [settings, setSettings] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', subject: '', message: '' });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [settingsRes, companiesRes] = await Promise.all([getSettings(), getCompanies()]);
        setSettings(settingsRes.data);
        setCompanies(companiesRes.data.filter(c => c.is_active));
      } catch (error) {
        console.error('Error loading contact data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.subject || !formData.message) {
      toast.error(t('يرجى ملء جميع الحقول المطلوبة', 'Please fill all required fields'));
      return;
    }

    setSubmitting(true);
    try {
      await createContact(formData);
      toast.success(t('تم إرسال رسالتك بنجاح', 'Your message has been sent successfully'));
      setFormData({ name: '', phone: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error(t('حدث خطأ أثناء الإرسال', 'Error sending message'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-raad-porcelain" data-testid="contact-page">
      <PageHero
        kicker={t('تواصل معنا', 'Contact Us')}
        title={t('نحن هنا لمساعدتك', 'We are Here to Help')}
        description={t('تواصل معنا للحصول على المزيد من المعلومات أو لأي استفسارات.', 'Contact us for more information or any inquiries.')}
      />

      <section className="pb-24">
        <div className="container mx-auto grid gap-10 px-6 lg:grid-cols-[1fr_380px]">
          <motion.div initial={{ opacity: 0, x: isRTL ? 30 : -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
            <form onSubmit={handleSubmit} className="rounded-[2rem] border border-raad-teal/15 bg-white p-7 shadow-soft md:p-9">
              <SectionTag>{t('رسالة مباشرة', 'Direct Message')}</SectionTag>
              <h2 className="mt-4 text-3xl font-black text-raad-charcoal">
                {t('أرسل لنا رسالة', 'Send us a Message')}
              </h2>

              <div className="mt-8 grid gap-6 sm:grid-cols-2">
                <div>
                  <Label htmlFor="name" className="font-bold text-raad-charcoal">{t('الاسم', 'Name')} *</Label>
                  <Input id="name" name="name" value={formData.name} onChange={handleChange} className={fieldClass} placeholder={t('أدخل اسمك', 'Enter your name')} data-testid="contact-name-input" />
                </div>
                <div>
                  <Label htmlFor="phone" className="font-bold text-raad-charcoal">{t('رقم الهاتف', 'Phone')} *</Label>
                  <Input id="phone" name="phone" dir="ltr" value={formData.phone} onChange={handleChange} className={`${fieldClass} phone-ltr`} placeholder={t('أدخل رقم هاتفك', 'Enter your phone')} data-testid="contact-phone-input" />
                </div>
                <div>
                  <Label htmlFor="email" className="font-bold text-raad-charcoal">{t('البريد الإلكتروني', 'Email')}</Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} className={fieldClass} placeholder={t('أدخل بريدك الإلكتروني', 'Enter your email')} data-testid="contact-email-input" />
                </div>
                <div>
                  <Label htmlFor="subject" className="font-bold text-raad-charcoal">{t('الموضوع', 'Subject')} *</Label>
                  <Input id="subject" name="subject" value={formData.subject} onChange={handleChange} className={fieldClass} placeholder={t('موضوع الرسالة', 'Message subject')} data-testid="contact-subject-input" />
                </div>
              </div>

              <div className="mt-6">
                <Label htmlFor="message" className="font-bold text-raad-charcoal">{t('الرسالة', 'Message')} *</Label>
                <Textarea id="message" name="message" value={formData.message} onChange={handleChange} className={`${fieldClass} min-h-[170px]`} placeholder={t('اكتب رسالتك هنا', 'Write your message here')} data-testid="contact-message-input" />
              </div>

              <Button type="submit" disabled={submitting} className="mt-7 w-full rounded-full bg-raad-charcoal py-6 text-white hover:bg-raad-teal" data-testid="contact-submit-btn">
                {submitting ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4 me-2" />
                    {t('إرسال الرسالة', 'Send Message')}
                  </>
                )}
              </Button>
            </form>
          </motion.div>

          <motion.aside initial={{ opacity: 0, x: isRTL ? -30 : 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="space-y-6">
            <CardShell className="p-7">
              <div className="relative z-10">
                <h3 className="text-2xl font-black text-raad-charcoal">{t('معلومات التواصل', 'Contact Information')}</h3>
                <div className="mt-6 space-y-4 text-sm font-semibold text-raad-graphite/70">
                  {settings?.contact_email && (
                    <a href={`mailto:${settings.contact_email}`} className="flex items-center gap-3 hover:text-raad-teal">
                      <Mail className="w-5 h-5 text-raad-teal" />
                      {settings.contact_email}
                    </a>
                  )}
                  {settings?.contact_phone && (
                    <a href={`tel:${settings.contact_phone}`} className="flex items-center gap-3 hover:text-raad-teal">
                      <Phone className="w-5 h-5 text-raad-teal" />
                      <PhoneText>{settings.contact_phone}</PhoneText>
                    </a>
                  )}
                  {(settings?.address_ar || settings?.address_en) && (
                    <div className="flex items-start gap-3">
                      <MapPin className="mt-0.5 w-5 h-5 flex-shrink-0 text-raad-teal" />
                      {t(settings.address_ar, settings.address_en)}
                    </div>
                  )}
                </div>
              </div>
            </CardShell>

            <CardShell className="p-7">
              <div className="relative z-10">
                <h3 className="text-xl font-black text-raad-charcoal">{t('شركاتنا', 'Our Companies')}</h3>
                <div className="mt-5 space-y-4">
                  {companies.slice(0, 5).map((company) => (
                    <Link key={company.id} to={`/companies/${company.slug}`} className="flex items-center gap-3 rounded-2xl p-2 transition-colors hover:bg-raad-mist">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-raad-mist">
                        {company.logo_url ? (
                          <img src={company.logo_url} alt={t(company.name_ar, company.name_en)} className="h-7 w-7 object-contain" />
                        ) : (
                          <Building2 className="h-5 w-5 text-raad-teal" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-black text-raad-charcoal">{t(company.name_ar, company.name_en)}</p>
                        {company.contact_phone && <PhoneText className="text-xs text-raad-graphite/50">{company.contact_phone}</PhoneText>}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </CardShell>
          </motion.aside>
        </div>

        {settings?.map_lat && settings?.map_lng && (
          <div className="container mx-auto mt-10 px-6">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="overflow-hidden rounded-[2rem] border border-white bg-white shadow-soft">
              <iframe
                title="Location Map"
                src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${settings.map_lat},${settings.map_lng}&zoom=15`}
                width="100%"
                height="420"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
              />
            </motion.div>
          </div>
        )}
      </section>
    </div>
  );
};

export default ContactPage;
